import { supabase } from "@/integrations/supabase/client";

const APP_VERSION = (import.meta.env.VITE_APP_VERSION as string) || "dev";

// In-memory dedupe so a single repeated error doesn't spam the table.
const recent = new Map<string, number>();
const DEDUPE_MS = 30_000;

function shouldLog(key: string): boolean {
  const now = Date.now();
  const last = recent.get(key);
  if (last && now - last < DEDUPE_MS) return false;
  recent.set(key, now);
  // Trim
  if (recent.size > 50) {
    const cutoff = now - DEDUPE_MS;
    for (const [k, t] of recent) if (t < cutoff) recent.delete(k);
  }
  return true;
}

export async function logClientError(
  message: string,
  stack?: string | null,
  routeOverride?: string,
): Promise<void> {
  try {
    if (!message) return;
    const route = routeOverride ?? (typeof window !== "undefined" ? window.location.pathname : null);
    const key = `${route}::${message.slice(0, 200)}`;
    if (!shouldLog(key)) return;

    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData?.session?.user?.id ?? null;

    await supabase.from("client_error_log").insert({
      user_id: userId,
      route,
      message: message.slice(0, 2000),
      stack: stack ? stack.slice(0, 8000) : null,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      app_version: APP_VERSION,
    });
  } catch {
    // Never let logging break the app.
  }
}

let installed = false;
export function installGlobalErrorHandlers() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (event) => {
    const msg = event?.message || String(event?.error?.message || "Unknown error");
    const stack = event?.error?.stack ?? null;
    void logClientError(msg, stack);
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason: any = event?.reason;
    const msg =
      (reason && (reason.message || reason.toString?.())) || "Unhandled promise rejection";
    const stack = reason?.stack ?? null;
    void logClientError(String(msg), stack);
  });
}